import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import IconButton from '../../shared/components/IconButton';

/**
 * WelcomeStepCard
 * 
 * A card component for displaying setup steps in the welcome screen.
 * 
 * Props:
 *  - stepNumber: The step number (1, 2, 3, etc.)
 *  - category: Category label (e.g., "GETTING STARTED")
 *  - categoryColor: Color variant for category ('green', 'purple', 'teal')
 *  - title: Main title of the step
 *  - icon: Bootstrap icon class for the title icon
 *  - items: Array of { id, label, completed, path? } for checklist (path = link target)
 *  - status: 'completed' | 'active' | 'locked'
 *  - buttonText: Text for action button (when active)
 *  - lockedMessage: Message to show when locked
 *  - onAction: Callback when action button clicked
 */
export default function WelcomeStepCard({
  stepNumber,
  category,
  categoryColor = 'green',
  title,
  icon,
  items = [],
  status = 'active',
  buttonText = "Let's go",
  lockedMessage = 'Complete previous step first',
  onAction,
}) {
  const isCompleted = status === 'completed';
  const isLocked = status === 'locked';
  const isActive = status === 'active';

  const categoryColorClass = {
    green: 'text-success',
    purple: 'welcome-text-purple',
    teal: 'welcome-text-teal',
  }[categoryColor] || 'text-muted';

  const stepNumberClass = classNames('welcome-step-number', {
    'welcome-step-number-completed': isCompleted,
    'welcome-step-number-active': isActive,
    'welcome-step-number-locked': isLocked,
  });

  const iconBgClass = classNames('welcome-icon-circle', {
    'welcome-icon-circle-green': categoryColor === 'green',
    'welcome-icon-circle-purple': categoryColor === 'purple',
    'welcome-icon-circle-teal': categoryColor === 'teal',
  });

  return (
    <div className="col-lg-4 col-md-6 mb-3">
      <div
        className={classNames('card h-100 welcome-step-card', {
          'welcome-step-card-active': isActive,
          'welcome-step-card-locked': isLocked,
        })}
      >
        <div className="card-body p-4 d-flex flex-column">
          {/* Header: Step badge + completion indicator */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <span className={stepNumberClass}>{stepNumber}</span>
            {isCompleted && (
              <span className="welcome-check-complete">
                <i className="bi bi-check-lg" />
              </span>
            )}
            {isActive && <span className="welcome-step-indicator" />}
            {isLocked && <span className="welcome-step-indicator-locked" />}
          </div>

          {/* Category label */}
          <div className={`welcome-category-label ${categoryColorClass}`}>
            {category}
          </div>

          {/* Title with icon */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className={iconBgClass}>
              <i className={icon} />
            </span>
            <h5 className="mb-0 fw-bold">{title}</h5>
          </div>

          {/* Checklist items (links when item has path) */}
          <div className="welcome-checklist mb-3 flex-grow-1">
            {items.map((item, idx) => {
              const itemContent = (
                <>
                  <span
                    className={classNames('welcome-checkbox', {
                      'welcome-checkbox-checked': item.completed,
                    })}
                  >
                    {item.completed && <i className="bi bi-check" />}
                  </span>
                  <span className="welcome-checklist-label">{item.label}</span>
                </>
              );
              const itemClass = classNames('welcome-checklist-item', {
                'welcome-checklist-item-completed': item.completed,
                'welcome-checklist-item-locked': isLocked,
              });
              const key = item.id ?? idx;
              if (item.path) {
                return (
                  <Link
                    key={key}
                    to={item.path}
                    className={`${itemClass} welcome-checklist-item-link`}
                  >
                    {itemContent}
                  </Link>
                );
              }
              return (
                <div key={key} className={itemClass}>
                  {itemContent}
                </div>
              );
            })}
          </div>

          {/* Action area - always at bottom */}
          <div className="welcome-action-area">
            {isCompleted && (
              <div className="welcome-action-block welcome-action-completed">
                <i className="bi bi-check me-1" />
                Completed
              </div>
            )}
            {isActive && (
              <IconButton
                icon="bi-arrow-right"
                className="btn welcome-action-btn link-btn-purple w-100"
                onClick={onAction}
              >
                {buttonText}
              </IconButton>
            )}
            {isLocked && (
              <div className="welcome-action-block welcome-locked-message">
                <i className="bi bi-lock me-2" />
                {lockedMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

WelcomeStepCard.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  category: PropTypes.string.isRequired,
  categoryColor: PropTypes.oneOf(['green', 'purple', 'teal']),
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      completed: PropTypes.bool,
      path: PropTypes.string,
    })
  ),
  status: PropTypes.oneOf(['completed', 'active', 'locked']),
  buttonText: PropTypes.string,
  lockedMessage: PropTypes.string,
  onAction: PropTypes.func,
};
